/**
 * What the screen says when the target position was empty.
 *
 * Pure, so the wording can be tested without mounting anything: the
 * components around it only place the strings. Every sentence here is
 * the copy table of the UX gate (`company/missions/CU-869fbfnuy/
 * ux-review.md` §"Copy propuesto"), and the two cases it distinguishes
 * are the ones Thibault asked about.
 *
 * It reads the two amounts it needs from where the row already carries
 * them — the anchor from `derivation.reference`, our own price from
 * `market_pvp` — and not from the `incomplete_market` block, which no
 * longer repeats them: money nested inside the payload arrives as a
 * string because webback only normalises the top level (TL C1, and the
 * mine in `company/tech/legacy.md` O7).
 */

//: Mirrored from `Activity/index.jsx` on purpose. Importing them would
//: make the screen and its copy circular, and a shared module for four
//: lines is churn this task was not asked for.
const eur = (n) => (n == null ? '—' : `${Number(n).toFixed(2)} €`)
const totalOf = (daily, days) =>
	(daily == null ? null : (Number(days) > 0 ? Number(daily) * Number(days) : Number(daily)))

/** `<total> € (<daily> €/day)`, or just the one figure when the stay is
 *  a single day and the two would be the same number twice. */
function withDaily(daily, days) {
	const total = totalOf(daily, days)
	return Number(days) > 1 ? `${eur(total)} (${eur(daily)}/day)` : eur(total)
}

function common(row) {
	const d = row?.derivation
	const im = d?.incomplete_market
	if (!im) return null
	// The engine quantises the DAILY price — `money(dearest.price * 1.05)`
	// — and the screen shows totals, so multiplying the total by 1.05
	// publishes a price the engine never decided: 1139.87 € against the
	// 1139.82 € two lines below (QA, 2026-10-05). The step carries the
	// engine's own number; the arithmetic below is only the fallback.
	const step = (d.steps || []).find(x => x && x.step === 'incomplete_market')
	return {
		priced: step?.to != null ? Number(step.to) : null,
		im,
		n: Number(im.competitors),
		asked: im.target_position,
		reached: im.position_reached,
		uplift: im.uplift_pct,
		provider: d.reference_provider || 'the dearest competitor',
		anchor: d.reference,
		own: row.market_pvp,
	}
}

/** What the engine would have decided, to the cent and per day. Used
 *  only when the step is missing — an event written before the step was. */
function upliftedDaily(anchor, uplift) {
	if (anchor == null) return null
	return Math.round(Number(anchor) * (1 + Number(uplift) / 100) * 100) / 100
}

/**
 * The adjustment, when it produced a price.
 *
 * `null` on an ordinary decision and on both no-change exits — those
 * explain themselves in the Reason block, through `incompleteMarketNoChange`.
 */
export function incompleteMarketCopy(row, days) {
	const c = common(row)
	if (!c || c.im.outcome !== 'priced') return null
	const { n, asked, reached, uplift, provider, anchor } = c

	// Never read from `proposed_price`: that field is the PVP after the
	// competitor guard ran, so on a cell with `never_above_competitor`
	// the screen would publish `98.69 € + 5 % = 90.00 €`. What the caps
	// did belongs to the Limits section.
	const factor = 1 + Number(uplift) / 100
	const priced = c.priced != null ? c.priced : upliftedDaily(anchor, uplift)

	// Only when the strategy has one. A blank here would read "The
	// configured undefined % undercut", and no linter catches that
	// because it is a string, not a component (TL, 2026-10-05).
	const undercut = row.derivation?.params?.undercut?.value

	const subNotes = [
		`Priced from ${provider}, the dearest of the ${n} competitor`
			+ `${n === 1 ? '' : 's'}, at ${withDaily(anchor, days)}.`,
	]
	if (undercut != null) {
		subNotes.push(`The configured ${undercut} % undercut does not apply — `
			+ `there is nobody at position ${asked} to undercut.`)
	}
	// Both halves of "P of P" always, because `position_reached` is the
	// rank inside the in-scope subset while the popup's `position` is the
	// rank across the whole rate shop. The denominator is what keeps the
	// two numbers from being read as the same one (TL O8).
	subNotes.push(reached === asked
		? `Position reached: ${reached} of ${reached} — the position you configured.`
		: `Position reached: ${reached} of ${reached} — the dearest offer in this market. `
			+ `You asked for position ${asked}.`)

	return {
		label: 'Incomplete market adjustment',
		badge: 'Incomplete market',
		amount: `${eur(totalOf(anchor, days))} + ${uplift} % = ${eur(totalOf(priced, days))}`,
		subNotes,
		tooltip: `Your strategy targets position ${asked}, but only ${n} competitor`
			+ `${n === 1 ? ' was' : 's were'} in scope, so nobody holds that position and `
			+ `there is nobody to undercut. Instead of leaving the price untouched, the `
			+ `engine prices ${uplift} % above the dearest competitor in scope. `
			+ `The ${uplift} % is fixed in the engine and is not configurable.`,
		rail: {
			node1: `${provider} — dearest of ${n} competitor${n === 1 ? '' : 's'} in scope`,
			node1Note: `target position ${asked} is empty: only ${n} competitor`
				+ `${n === 1 ? '' : 's'} in scope, so there is nobody to undercut.`,
			step: `incomplete market · ${eur(totalOf(anchor, days))} × ${factor.toFixed(2)}`
				+ ` = ${eur(totalOf(priced, days))}`,
			stepNote: `${uplift} % above ${provider}, the dearest competitor in scope — `
				+ `fixed in the engine, not configurable.`
				+ (undercut != null
					? ` The configured ${undercut} % undercut does not apply: `
						+ `nobody holds position ${asked}.`
					: ''),
			node3: `target shelf price · lands at position ${reached} of ${reached}`,
		},
	}
}

/**
 * The two exits that decline to price, for the Reason block.
 *
 * `null` on an ordinary decision and on the priced case. The engine
 * writes a bare code into `reason`; this is the sentence it stands for,
 * plus the comparison that justifies it — without the numbers the
 * operator has to take the refusal on trust (UX gate, criterion 5).
 */
export function incompleteMarketNoChange(row, days) {
	const c = common(row)
	if (!c || (c.im.outcome !== 'already_dearest'
	           && c.im.outcome !== 'own_price_unknown')) return null
	const { n, asked, uplift, provider, anchor, own } = c

	const opening = `Target position ${asked} was empty: only ${n} competitor`
		+ `${n === 1 ? '' : 's'} in scope. The dearest, ${provider}, is at `
		+ `${withDaily(anchor, days)}`

	if (c.im.outcome === 'own_price_unknown') {
		return {
			badge: 'Incomplete market',
			reason: 'Incomplete market — we do not know what we charge in this market '
				+ 'today, so there was nothing to compare against.',
			detail: `${opening}. Our own price was not found in this rate shop, so the `
				+ `guard that decides whether the adjustment would raise us could not be `
				+ `evaluated, and the price was left untouched.`,
			tooltip: `Your strategy targets position ${asked}, but only ${n} competitor`
				+ `${n === 1 ? ' was' : 's were'} in scope, so nobody holds that position. `
				+ `The engine prices ${uplift} % above the dearest competitor instead of `
				+ `refusing — but only when that raises your price, and our own price is `
				+ `not in this rate shop, so the comparison could not be made.`,
		}
	}

	// No arithmetic here on purpose. The guard compares our price against
	// the ANCHOR, not against the uplifted price, so between the two the
	// adjustment would in fact have raised us — and saying "would give
	// Z € — below ours" is then false in front of the operator (QA,
	// 2026-10-05). What is always true is the comparison the guard made.
	return {
		badge: 'Incomplete market',
		reason: 'Incomplete market — we were already the dearest offer, so there was '
			+ 'nothing to price above.',
		detail: `${opening}; our price is ${withDaily(own, days)} — already above it. `
			+ `The incomplete-market adjustment prices above the dearest competitor in `
			+ `scope, and we are already there, so the price was left untouched.`,
		// Same correction as `detail`, and for the same reason: the guard
		// compares us against the anchor, so "it would have lowered it"
		// is false whenever we sit between the anchor and the anchor plus
		// the uplift. It survived here because the test only read
		// `detail` (QA v3, 2026-10-05).
		tooltip: `Your strategy targets position ${asked}, but only ${n} competitor`
			+ `${n === 1 ? ' was' : 's were'} in scope, so nobody holds that position. `
			+ `The engine prices ${uplift} % above the dearest competitor instead of `
			+ `refusing — but only when we are not already the dearest ourselves. `
			+ `Here we were, so nothing was changed. The ${uplift} % is fixed in the `
			+ `engine and is not configurable.`,
	}
}
